import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import { 
  Target, 
  Search, 
  Mic,
  Loader2,
  Save,
  RefreshCw
} from 'lucide-react';

const CertificationThresholdSettings = () => {
  const { api } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/rpat/certification/admin/threshold-settings');
      setSettings(response.data);
    } catch (err) {
      console.error('Failed to fetch threshold settings:', err);
      // Set defaults
      setSettings({
        job_simulation_min_score: 80.0,
        sourcing_lab_min_score: 80.0,
        role_play_min_score: 80.0,
        use_global_settings: true,
        score_range: { min: 70, max: 100 }
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const saveSettings = async () => {
    try {
      setSaving(true);
      await api.put('/rpat/certification/admin/threshold-settings', null, {
        params: {
          job_simulation_min_score: settings.job_simulation_min_score,
          sourcing_lab_min_score: settings.sourcing_lab_min_score,
          role_play_min_score: settings.role_play_min_score,
          use_global_settings: settings.use_global_settings
        }
      });
      toast.success('Certification threshold settings saved!');
    } catch (err) {
      console.error('Failed to save threshold settings:', err);
      toast.error(err.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card data-testid="certification-threshold-settings-card">
        <CardContent className="py-8 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="certification-threshold-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5 text-violet-600" />
          R-PAT Certification Thresholds
        </CardTitle>
        <CardDescription>
          Set minimum passing scores for Job Simulations, Sourcing Labs, and Role Play.
          Users must meet these thresholds to complete each phase.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Global Settings Toggle */}
        <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
          <div className="space-y-0.5">
            <Label className="text-base font-medium">Use Global Settings</Label>
            <p className="text-sm text-muted-foreground">
              Apply these thresholds to all users
            </p>
          </div>
          <Switch
            checked={settings?.use_global_settings}
            onCheckedChange={(checked) => setSettings({ ...settings, use_global_settings: checked })}
            data-testid="cert-threshold-global-toggle"
          />
        </div>

        {/* Job Simulation Threshold */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Target className="h-4 w-4 text-violet-500" />
              Job Simulation Minimum Score
            </Label>
            <Badge variant="secondary" className="text-lg px-3">
              {settings?.job_simulation_min_score}%
            </Badge>
          </div>
          <Slider
            value={[settings?.job_simulation_min_score || 80]}
            onValueChange={([value]) => setSettings({ ...settings, job_simulation_min_score: value })}
            min={70}
            max={100}
            step={1}
            className="w-full"
            data-testid="job-simulation-slider"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>70% (Minimum)</span>
            <span>100% (Perfect)</span>
          </div>
        </div>

        {/* Sourcing Lab Threshold */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Search className="h-4 w-4 text-amber-500" />
              Sourcing Lab Minimum Score
            </Label>
            <Badge variant="secondary" className="text-lg px-3">
              {settings?.sourcing_lab_min_score}%
            </Badge>
          </div>
          <Slider
            value={[settings?.sourcing_lab_min_score || 80]}
            onValueChange={([value]) => setSettings({ ...settings, sourcing_lab_min_score: value })}
            min={70}
            max={100}
            step={1}
            className="w-full"
            data-testid="sourcing-lab-slider"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>70% (Minimum)</span>
            <span>100% (Perfect)</span>
          </div>
        </div>

        {/* Role Play Threshold */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Mic className="h-4 w-4 text-emerald-500" />
              Role Play Minimum Score
            </Label>
            <Badge variant="secondary" className="text-lg px-3">
              {settings?.role_play_min_score}%
            </Badge>
          </div>
          <Slider
            value={[settings?.role_play_min_score || 80]}
            onValueChange={([value]) => setSettings({ ...settings, role_play_min_score: value })}
            min={70}
            max={100}
            step={1}
            className="w-full"
            data-testid="role-play-slider"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>70% (Minimum)</span>
            <span>100% (Perfect)</span>
          </div>
        </div>

        {/* Info Box */}
        <div className="p-4 bg-violet-50 border border-violet-200 rounded-lg">
          <p className="text-sm text-violet-700">
            <strong>Note:</strong> Users must score at or above these thresholds to mark tasks as completed. 
            Lower scores will require retaking the simulation/lab/scenario.
            Default is 80% for all three categories.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-4">
          <Button 
            onClick={saveSettings}
            disabled={saving}
            className="flex-1 bg-gradient-to-r from-violet-500 to-purple-600"
            data-testid="save-cert-threshold-btn"
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Settings
          </Button>
          <Button 
            variant="outline"
            onClick={fetchSettings}
            disabled={loading}
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Reset
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default CertificationThresholdSettings;
